import { useSelector } from 'react-redux';
import { getTransformer, filterDefaultFolderFile } from '../utils';
import { useCallback, useMemo } from 'react';
import useOpenApiParser from '../../../hooks/useOpenApiParser';

/**
 * Custom hook that transforms a given tree structure using a transformer function.
 *
 * This hook utilizes memoization to ensure that the transformation is only recalculated
 * when the input tree changes, improving performance for static tree data.
 *
 * Enhanced with useOpenApiParser integration for consistent OpenAPI data parsing
 * across the application. Pre-computes API structures to improve performance.
 *
 * @returns {Function} - The transformer function that accepts (tree, rootId) parameters.
 */

export function useTransformer() {
  const { openApispec, activeWorkspace } = useSelector((state) => state.organization);
  const { parseTags, parseApiStructure } = useOpenApiParser();

  // Extract OpenAPI tags using the parser hook for consistency
  const openapiTags = useMemo(
    () =>
      Object.keys(openApispec || {})?.reduce((acc, key) => {
        const tags = parseTags(openApispec[key]);
        acc[key] = tags.map((tag) => tag.name);
        return acc;
      }, {}),
    [openApispec, parseTags]
  );

  // Pre-compute parsed API structures for all OpenAPI specs
  const parsedApiStructures = useMemo(
    () =>
      Object.keys(openApispec || {})?.reduce((acc, key) => {
        acc[key] = parseApiStructure(openApispec[key], key);
        return acc;
      }, {}),
    [openApispec, parseApiStructure]
  );

  const transfomer = useCallback(
    (tree, rootId) => {
      if (!tree) return [];
      const transformer = getTransformer();
      return filterDefaultFolderFile(
        transformer(tree, rootId, openapiTags, openApispec, {
          wsMetaInfo: activeWorkspace?.meta_info?.sidebar_info,
          parsedApiStructures, // Pass the pre-computed structures
        })
      );
    },
    [openApispec, openapiTags, activeWorkspace, parsedApiStructures]
  );

  return transfomer;
}

export default useTransformer;
